import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Send } from 'lucide-react';
import { useGlobalNotificationsWs } from '../hooks/notifications/useGlobalNotificationsWs';
import { useDraftRefreshSignal } from '../hooks/notifications/draftRefresh';
import NotificationBell from './notifications/NotificationBell';
import ChatNotificationsPanel from './notifications/ChatNotificationsPanel';
import TaskNotificationsBell from './notifications/TaskNotificationsBell';
import ProfileTab from './profile/ProfileTab';
import SidebarAttendanceClock from './SidebarAttendanceClock';
import { selectTotalUnread } from '../redux/slices/chatUnread';
import { useAuth } from '../contexts/AuthContext';
import { useAccess } from '../contexts/AccessContext';
import { tasksApi } from '../hrms/api/tasks';
import { isAdminRole } from '../hrms/utils/rolePermissions';
import { getMissedCount, subscribeMissed } from '../utils/call/missedBadge';
import { PRIMARY_NAV_ITEMS, type NavItem } from './primaryNav';

export default function AppSidebar() {
  // The rail is permanently the icon rail — it no longer expands into a wide
  // labelled menu. Sub-navigation for Tasks lives in the TaskNavPanel that opens
  // beside it (like the Chat conversation list).
  const collapsed = true;
  useGlobalNotificationsWs();
  const chatUnreadTotal = useSelector(selectTotalUnread);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasAccess, isPathVisible } = useAccess();
  const isAdmin = isAdminRole(user?.role || '');

  // Access-profile gating: hide task panels the assigned profile doesn't enable.
  // Native items (Chat/Calls/Calendar) are never gated. No profile → show all.
  const navItems = useMemo<NavItem[]>(() => {
    return PRIMARY_NAV_ITEMS.map((item) => {
      if (item.kind === 'leaf') {
        return !hasAccess || isPathVisible(item.to) ? item : null;
      }
      const children = item.children.filter((c) => !hasAccess || isPathVisible(c.to));
      return children.length ? { ...item, children } : null;
    }).filter(Boolean) as NavItem[];
  }, [hasAccess, isPathVisible]);
  const [draftCount, setDraftCount] = useState(0);
  const draftMountedRef = useRef(true);
  useEffect(() => () => { draftMountedRef.current = false; }, []);

  const [missedCount, setMissedCount] = useState(getMissedCount());
  useEffect(() => subscribeMissed(() => setMissedCount(getMissedCount())), []);

  const refreshDraftCount = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await tasksApi.listDrafts(true);
      if (!draftMountedRef.current) return;
      const count = res?.data?.count ?? 0;
      setDraftCount(Number.isFinite(count) ? count : 0);
    } catch {
      // swallow — sidebar shouldn't break on transient errors
    }
  }, [isAdmin]);

  // Load once, then refresh off the WS draft_changed ping (+ a throttled
  // refetch when the window reappears). The old 60s poll is gone.
  useEffect(() => {
    if (!isAdmin) {
      setDraftCount(0);
      return;
    }
    refreshDraftCount();
  }, [isAdmin, refreshDraftCount]);

  useDraftRefreshSignal(refreshDraftCount, isAdmin);

  const width = collapsed ? 72 : 240;

  return (
    <aside className={`gt-sidebar${collapsed ? ' is-collapsed' : ''}`} style={{ width }}>
      {/* Top rail pocket: keeps macOS traffic-light clearance while hosting
          the attendance clock control above notifications. */}
      <div className="gt-sidebar-header">
        <SidebarAttendanceClock />
      </div>

      <div className={`gt-sidebar-bells${collapsed ? ' is-collapsed' : ''}`}>
        <NotificationBell
          icon={<Send className="w-4 h-4" />}
          count={chatUnreadTotal}
          ariaLabel="Chat notifications"
        >
          {(close) => <ChatNotificationsPanel onItemSelected={close} />}
        </NotificationBell>
        <TaskNotificationsBell />
      </div>

      <nav className="gt-sidebar-nav">
        {navItems.map((item) => {
          if (item.kind === 'leaf') {
            const { to, label, Icon } = item;
            return (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `gt-sidebar-item${isActive ? ' is-active' : ''}`
                }
                title={collapsed ? label : undefined}
              >
                <Icon className="gt-sidebar-icon" />
                <span className="gt-sidebar-label">{label}</span>
                {to === '/calls' && missedCount > 0 && (
                  <span
                    data-testid="sidebar-missed-badge"
                    style={{
                      minWidth: 18,
                      height: 18,
                      padding: '0 6px',
                      borderRadius: 9,
                      background: '#ef4444',
                      color: 'white',
                      fontSize: 11,
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginLeft: 'auto',
                    }}
                  >
                    {missedCount > 99 ? '99+' : missedCount}
                  </span>
                )}
              </NavLink>
            );
          }

          // Task Management is a single icon now: clicking it enters the
          // section (navigates to the first visible task page) and the
          // TaskNavPanel opens beside the rail with the sub-navigation —
          // mirroring how clicking Chat opens the conversation list. The rail
          // itself no longer expands into a submenu.
          const { id, label, Icon, children } = item;
          const groupActive = children.some((c) => location.pathname.startsWith(c.to));
          const target = children[0]?.to || '/tasks/list';
          const showDraftBadge = isAdmin && draftCount > 0;

          return (
            <button
              key={id}
              type="button"
              className={`gt-sidebar-item${groupActive ? ' is-active' : ''}`}
              onClick={() => navigate(target)}
              title={collapsed ? label : undefined}
            >
              <Icon className="gt-sidebar-icon" />
              <span className="gt-sidebar-label">{label}</span>
              {showDraftBadge && (
                <span
                  data-testid="sidebar-draft-badge"
                  style={{
                    minWidth: 18,
                    height: 18,
                    padding: '0 6px',
                    borderRadius: 9,
                    background: '#ef4444',
                    color: 'white',
                    fontSize: 11,
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: 'auto',
                  }}
                >
                  {draftCount > 99 ? '99+' : draftCount}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <div style={{ borderTop: '1px solid var(--gt-divider)', padding: 12 }}>
        <ProfileTab collapsed={collapsed} />
      </div>
    </aside>
  );
}
